import { FormControl, Grid, InputLabel, MenuItem, Select } from '@material-ui/core';
import AddIcon from '@mui/icons-material/Add';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import { AxiosError } from 'axios';
import React, { useEffect, useState } from 'react';
import { useLayoutContext } from '../../Context/Layout.context';
import {
  CriteriaSetup,
  CriteriaSetupDto,
  CriteriaSetupResponseDto,
  CriteriaSetupTemplate,
  ICriteriaSetupDetails,
  saveCriteriaSetupErrors,
  Scheme,
  Score,
} from '../../models/session.types';
import { getCriteriaSetup, saveCriteriaSetup } from '../../services/exams/sessions/sessions.service';
import { ButtonOne } from '../ButtonOne/ButtonOne.component';
import { BreadcrumbLevel } from '../DialogBreadcrumbs/DialogBreadcrumbs.types';
import { LeavePrompt } from '../Prompt/LeavePrompt/LeavePrompt.component';
import { Prompt, SubText } from '../Prompt/Prompt.component';
import { WorkMode } from '../SessionTree/SessionTree.types';
import ActionButtons from './ActionButtons';
import { useStyles } from './CriteriaSetupDetails.styles';
import { useStyles as sessionDetails } from './SessionDetails.styles';
import { DetailsProps } from './DetailsProps.types';
import EditButton from './EditButton.component';
import { blockIconForInput } from './BlockItemForInput';

interface CriteriaSetupDetailsProps extends DetailsProps {
  data: ICriteriaSetupDetails;
  isReadOnly: () => boolean;
  workMode: WorkMode;
}

const defaultCriteriaSet = {
  id: '',
  leadingSchema: { id: '', value: 0, isError: false },
  dependentSchema: { id: '', value: 0, isError: false },
  fromExamScoreValueForLeadingSchema: { id: '', value: 0 },
  toExamScoreValueForLeadingSchema: { id: '', value: 0 },
  fromExamScoreValueForDependentSchema: { id: '', value: 0 },
  toExamScoreValueForDependentSchema: { id: '', value: 0 },
};

const defaultCriteriaSetupTemplate = {
  id: '',
  leadingSchemas: [],
  dependentSchemas: [],
};

const CriteriaSetupDetails: React.FC<CriteriaSetupDetailsProps> = (props) => {
  const classes = useStyles();
  const sessionDetailsClasses = sessionDetails();

  const [criteriaSetupTemplate, setCriteriaSetupTemplate] =
    useState<CriteriaSetupTemplate>(defaultCriteriaSetupTemplate);
  const [criteriaSetupItems, setCriteriaSetupItems] = useState<CriteriaSetup[]>([]);
  const [initialCriteriaSetupItemsLength, setInitialCriteriaSetupItemsLength] = useState<number>(0);
  const [scores, setScores] = useState<Score[]>([]);
  const [schemeNames, setSchemeNames] = useState<any[]>([]);
  const [isDataChanged, setIsDataChanged] = useState<boolean>(false);
  const [isAddButton, setIsAddButton] = useState<boolean>(false);
  const [isLeavePromptOpen, setIsLeavePromptOpen] = useState<boolean>(false);

  const [saveValidationError, setSaveValidationError] = useState<
    'ExamScoringValuesAreCovering' | 'EmptySetForDependentCritera' | 'IndirectConnections' | null
  >(null);

  const [wrongCriteriaIds, setWrongCriteriaIds] = useState<string[]>([]);
  const [wrongCriteriaPairsIds, setWrongCriteriaPairsIds] = useState<string[][]>([]);

  const [errorCriteriaPairs, setErrorCriteriaPairs] = useState<SubText[]>([]);

  const { showLoader, openAlert } = useLayoutContext();

  const isEditWorkMode = () => {
    return props.workMode === WorkMode.EDIT;
  };

  const handleGetCriteriaSetup = () => {
    showLoader(true);
    if (props.data.id)
      getCriteriaSetup(props.data.id)
        .then(
          (result: CriteriaSetupResponseDto[]) => {
            const transformedResult: CriteriaSetup[] = [];
            if (result) {
              if (schemeNames.length) {
                result.forEach((e: CriteriaSetupResponseDto) =>
                  transformedResult.push({
                    id: e.criteriaSetupId,
                    leadingSchema: {
                      id: e.leadingSchemaId,
                      value: schemeNames.find((e) => e.id === e.leadingSchemaId)?.name,
                    },
                    dependentSchema: {
                      id: e.dependentSchemaId,
                      value: schemeNames.find((e) => e.id === e.dependentSchemaId)?.name,
                    },
                    fromExamScoreValueForLeadingSchema: {
                      id: e.scoresForLeadingSchema[0].examScoreValueId,
                      value: e.scoresForLeadingSchema[0].score,
                    },
                    toExamScoreValueForLeadingSchema: {
                      id: e.scoresForLeadingSchema[1].examScoreValueId,
                      value: e.scoresForLeadingSchema[1].score,
                    },
                    fromExamScoreValueForDependentSchema: {
                      id: e.scoresForDependentSchema[0].examScoreValueId,
                      value: e.scoresForDependentSchema[0].score,
                    },
                    toExamScoreValueForDependentSchema: {
                      id: e.scoresForDependentSchema[1].examScoreValueId,
                      value: e.scoresForDependentSchema[1].score,
                    },
                  }),
                );
              }
            }
            if (transformedResult.length) {
              setCriteriaSetupItems(transformedResult);
              setInitialCriteriaSetupItemsLength(transformedResult.length);
              setIsDataChanged(false);
            }
          },
          () => {
            openAlert('error');
          },
        )
        .finally(() => {
          showLoader(false);
        });
  };

  useEffect(() => {
    if (schemeNames.length) {
      handleGetCriteriaSetup();
    }
  }, [props.data.id, schemeNames]);

  useEffect(() => {
    if (criteriaSetupItems.length) {
      setIsDataChanged(true);
    }
  }, [criteriaSetupItems]);

  const getCriterioNameFromId = (id: string) => {
    if (props.data.schemas) {
      const criterio = props.data.schemas.find((e) => e.id === id);
      if (criterio) {
        return `${criterio.label}_${criterio.name}`;
      }
    }
  };

  const getWrongCriteriaNamesList = () => {
    if (props.data.schemas) {
      return props.data.schemas
        .filter((e) => wrongCriteriaIds.includes(e.id))
        .map((e) => `${e.label}_${e.name}`)
        .join(', ');
    }
  };

  const getWrongCriteriaPairsNamesList = () => {
    if (props.data.schemas) {
      const pairList: SubText[] = [];
      wrongCriteriaPairsIds.forEach((e, index) => {
        pairList.push({
          text: `Kryterium: ${getCriterioNameFromId(e[0])}, a kryterium: ${getCriterioNameFromId(e[1])}${
            index < wrongCriteriaPairsIds.length - 1 ? ',' : ''
          }`,
        });
        setErrorCriteriaPairs(pairList);
      });
    }
  };

  useEffect(() => {
    if (wrongCriteriaPairsIds.length > 0) getWrongCriteriaPairsNamesList();
  }, [wrongCriteriaPairsIds]);

  useEffect(() => {
    if (props.data.schemas && props.data.schemas.length) {
      const schemas = props.data.schemas;
      setCriteriaSetupTemplate((prevState) => {
        return {
          ...prevState,
          leadingSchemas: schemas,
          dependentSchemas: schemas,
        };
      });

      const scores: Score[] = [];
      const schemeNames: { id: string; name: string }[] = [];
      props.data.schemas.forEach((scheme) => scheme.scores.forEach((score) => scores.push(score)));
      props.data.schemas.forEach((scheme) => schemeNames.push({ id: scheme.id, name: scheme.name }));

      setScores(scores);
      setSchemeNames(schemeNames);
      getWrongCriteriaPairsNamesList();
    }
  }, [props.data.schemas]);

  const getBodyRequest = (): CriteriaSetupDto[] => {
    return criteriaSetupItems.map((item) => ({
      criteriaSetupId: item.id.includes('newItem') ? null : item.id,
      leadingSchemaId: item.leadingSchema.id,
      dependentSchemaId: item.dependentSchema.id,
      fromExamScoreValueForLeadingSchemaId: item.fromExamScoreValueForLeadingSchema.id,
      toExamScoreValueForLeadingSchemaId: item.toExamScoreValueForLeadingSchema.id,
      fromExamScoreValueForDependentSchemaId: item.fromExamScoreValueForDependentSchema.id,
      toExamScoreValueForDependentSchemaId: item.toExamScoreValueForDependentSchema.id,
    }));
  };

  const saveChanges = async () => {
    if (props.data.gradeRuleId) {
      showLoader(true);
      saveCriteriaSetup(props.data.gradeRuleId, getBodyRequest())
        .then(
          () => {
            openAlert('success');
            setIsDataChanged(false);
            if (props.data.gradeRuleId) {
              props.onSave({ id: props.data.gradeRuleId }, false);
            }
          },
          (error: AxiosError) => {
            if (!navigator.onLine) {
              openAlert('error', 'W wyniku braku połączenia z internetem zapisywanie danych nie powiodło się.');
            } else {
              const errorData: saveCriteriaSetupErrors = error.response?.data;
              if (errorData) {
                if (errorData.IndirectConnections) {
                  // rule 3
                  const indirectIdPairs = errorData.IndirectConnections.map((str) => {
                    const match = str.match(/\d+/g);
                    if (match !== null) return [match[0], match[1]];
                    else return [];
                  }).filter((item) => item.length > 0);
                  setWrongCriteriaPairsIds(indirectIdPairs);
                  setSaveValidationError('IndirectConnections');
                  return;
                }
                if (errorData.ExamScoringValuesAreCovering) {
                  // rule 1
                  setWrongCriteriaIds(errorData.ExamScoringValuesAreCovering);
                  setSaveValidationError('ExamScoringValuesAreCovering');
                  return;
                }
                if (errorData.EmptySetForDependentCritera) {
                  //rule 2
                  setWrongCriteriaIds(errorData.EmptySetForDependentCritera);
                  setSaveValidationError('EmptySetForDependentCritera');
                  return;
                }
              }
            }
          },
        )
        .finally(() => {
          showLoader(false);
        });
    }
  };

  const revert = () => {
    props.onClose(props.data, BreadcrumbLevel.CRITERIA_SETUP, revert);
  };
  const closeHandle = () => {
    setIsLeavePromptOpen(false);
    setCriteriaSetupItems([]);
    handleGetCriteriaSetup();
    revert();
  };

  const handleEditMode = () => {
    props.onEdit(props.data);
  };

  const handleLeadingCriterionChange = (event: React.ChangeEvent<{ value: unknown }>, criterion: CriteriaSetup) => {
    setCriteriaSetupItems((prevState) =>
      prevState.map((item) =>
        item.id !== criterion.id
          ? item
          : {
              ...item,
              leadingSchema: { id: event.target.value as string, value: criterion.leadingSchema.value },
              fromExamScoreValueForLeadingSchema: defaultCriteriaSet.fromExamScoreValueForLeadingSchema,
              toExamScoreValueForLeadingSchema: defaultCriteriaSet.toExamScoreValueForLeadingSchema,
            },
      ),
    );
  };

  const handleDependentCriterionChange = (event: React.ChangeEvent<{ value: unknown }>, criterion: CriteriaSetup) => {
    setCriteriaSetupItems((prevState) =>
      prevState.map((item) =>
        item.id !== criterion.id
          ? item
          : {
              ...item,
              dependentSchema: { id: event.target.value as string, value: criterion.dependentSchema.value },
              fromExamScoreValueForDependentSchema: defaultCriteriaSet.fromExamScoreValueForDependentSchema,
              toExamScoreValueForDependentSchema: defaultCriteriaSet.toExamScoreValueForDependentSchema,
            },
      ),
    );
  };

  const handleLeadingCriterioScoresChangeFrom = (
    event: React.ChangeEvent<{ value: unknown }>,
    criterion: CriteriaSetup,
  ) => {
    const newValue = event.target.value as string;
    const score = scores.find((e) => e.id === newValue)?.value;
    const isScoreToFix = score ? criterion.toExamScoreValueForLeadingSchema.value < score : false;

    setCriteriaSetupItems((prevState) =>
      prevState.map((item) =>
        item.id !== criterion.id
          ? item
          : {
              ...item,
              fromExamScoreValueForLeadingSchema: {
                id: newValue,
                value: score ?? 0,
              },
              toExamScoreValueForLeadingSchema: {
                id: isScoreToFix ? newValue : criterion.toExamScoreValueForLeadingSchema.id,
                value: criterion.toExamScoreValueForLeadingSchema.value,
              },
            },
      ),
    );
  };

  const isScoreDisabled = (leadingSchemaScore: number, id: string) => {
    if (scores) {
      return scores.filter((e) => e.id === id)[0].value < leadingSchemaScore;
    }
  };

  const handleLeadingCriterioScoresChangeTo = (
    event: React.ChangeEvent<{ value: unknown }>,
    criterion: CriteriaSetup,
  ) => {
    const newValue = event.target.value as string;
    const score = scores.find((e) => e.id === newValue)?.value;
    const scoreId = scores.find((e) => e.id === newValue)?.id;
    if (scoreId) {
      if (isScoreDisabled(criterion.fromExamScoreValueForLeadingSchema.value, scoreId)) return;
    }
    setCriteriaSetupItems((prevState) =>
      prevState.map((item) =>
        item.id !== criterion.id
          ? item
          : {
              ...item,
              toExamScoreValueForLeadingSchema: {
                id: newValue,
                value: score ?? 0,
              },
            },
      ),
    );
  };

  const handleDependentCriterioScoresChangeFrom = (
    event: React.ChangeEvent<{ value: unknown }>,
    criterion: CriteriaSetup,
  ) => {
    const newValue = event.target.value as string;
    const score = scores.find((e) => e.id === newValue)?.value;
    const isScoreToFix = score ? criterion.toExamScoreValueForDependentSchema.value < score : false;
    setCriteriaSetupItems((prevState) =>
      prevState.map((item) =>
        item.id !== criterion.id
          ? item
          : {
              ...item,
              fromExamScoreValueForDependentSchema: {
                id: newValue,
                value: score ?? 0,
              },
              toExamScoreValueForDependentSchema: {
                id: isScoreToFix ? newValue : criterion.toExamScoreValueForDependentSchema.id,
                value: criterion.toExamScoreValueForDependentSchema.value,
              },
            },
      ),
    );
  };

  const handleDependentCriterioScoresChangeTo = (
    event: React.ChangeEvent<{ value: unknown }>,
    criterion: CriteriaSetup,
  ) => {
    const newValue = event.target.value as string;
    const score = scores.find((e) => e.id === newValue)?.value;
    const scoreId = scores.find((e) => e.id === newValue)?.id;
    if (scoreId) {
      if (isScoreDisabled(criterion.fromExamScoreValueForDependentSchema.value, scoreId)) return;
    }

    setCriteriaSetupItems((prevState) =>
      prevState.map((item) =>
        item.id !== criterion.id
          ? item
          : {
              ...item,
              toExamScoreValueForDependentSchema: {
                id: newValue,
                value: score ?? 0,
              },
            },
      ),
    );
  };

  const handleAddCriteriaSetupItem = () => {
    if (criteriaSetupTemplate) {
      setCriteriaSetupItems((prevState) => {
        return [
          ...prevState,
          {
            ...defaultCriteriaSet,
            id: 'newItem' + criteriaSetupItems.length,
          },
        ];
      });
    }
  };

  const handleDeleteCriteriaSetupItem = (index: number) => {
    setCriteriaSetupItems((prevState) => {
      return [...prevState.slice(0, index), ...prevState.slice(index + 1)];
    });
  };

  const editingDisabled = props.isReadOnly();

  const isAnyItemMissingData = (): boolean => {
    return criteriaSetupItems.some(
      (item) =>
        item.leadingSchema.id === '' ||
        item.dependentSchema.id === '' ||
        item.fromExamScoreValueForLeadingSchema.id === '' ||
        item.toExamScoreValueForLeadingSchema.id === '' ||
        item.fromExamScoreValueForDependentSchema.id === '' ||
        item.toExamScoreValueForDependentSchema.id === '',
    );
  };

  const isSaveDisabled = () => {
    return isAnyItemMissingData() || (!isDataChanged && criteriaSetupItems.length === initialCriteriaSetupItemsLength);
  };

  useEffect(() => {
    setIsAddButton(
      props.workMode === WorkMode.EDIT &&
        ((props.data.schemas && props.data.schemas.length > 1) || criteriaSetupItems.length === 0),
    );
  }, [props.workMode, props.data.schemas, criteriaSetupItems]);

  const InputLabelStyle = (schemeId: string) => {
    return {
      color: schemeId === '' ? '#D3D3D3' : '#2C2D3E',
      left: '14px',
      top: '-8px',
    };
  };

  const getOverlappingCriterions = (): SubText[] => {
    const uniqueCriteriaEntries = new Set<string>();
    const criterion = criteriaSetupItems.filter((item) => wrongCriteriaIds.includes(item.leadingSchema.id));
    if (criterion.length > 0) {
      criterion.forEach((criterion) =>
        uniqueCriteriaEntries.add(
          `kryterium: ${getCriterioNameFromId(criterion.leadingSchema.id)} i ${getCriterioNameFromId(
            criterion.dependentSchema.id,
          )}`,
        ),
      );
    }
    return Array.from(uniqueCriteriaEntries.values()).map((item) => {
      return { text: item };
    });
  };

  const getMainText = () => {
    switch (saveValidationError) {
      case 'ExamScoringValuesAreCovering':
        return 'Nie można zdefiniować punktacji dla relacji:';
      case 'EmptySetForDependentCritera':
        return 'Wystąpił błąd, ponieważ końcowy zbiór punktów jest pusty dla kryteriów:';
      case 'IndirectConnections':
        return 'Wystąpił błąd, ponieważ w zdefiniowanych parach kryteriów zaistniała relacja dwustronna między:';
      default:
        return undefined;
    }
  };

  const getSubTexts = () => {
    switch (saveValidationError) {
      case 'ExamScoringValuesAreCovering':
        return getOverlappingCriterions();
      case 'EmptySetForDependentCritera':
        return [
          {
            text: getWrongCriteriaNamesList(),
          },
        ];
      case 'IndirectConnections':
        return errorCriteriaPairs;
      default:
        return undefined;
    }
  };

  return (
    <>
      {props.data && criteriaSetupTemplate.leadingSchemas.length && criteriaSetupTemplate.dependentSchemas.length && (
        <Grid container alignContent="space-between" alignItems="flex-end" style={{ height: '490px' }}>
          <Grid container justifyContent="flex-end" alignContent="flex-end">
            {props.workMode !== WorkMode.EDIT && (
              <EditButton workMode={props.workMode} handleEditMode={handleEditMode} isDisabled={editingDisabled} />
            )}
          </Grid>
          <Grid container justifyContent="center" alignContent="center" className={sessionDetailsClasses.headerRow}>
            {props.workMode === WorkMode.VIEW && (
              <div className={sessionDetailsClasses.headerText}>Kryteria zależne</div>
            )}
            {props.workMode === WorkMode.EDIT && (
              <div className={sessionDetailsClasses.headerText}>Edycja kryteriów zależnych</div>
            )}
          </Grid>

          <div
            style={{ maxHeight: '280px', overflowY: 'auto', overflowX: 'hidden', width: '100%', paddingBottom: '20px' }}
          >
            {criteriaSetupItems.map((criterion: CriteriaSetup, index) => (
              <React.Fragment key={`${criterion.criteriaSetupId} ${index}`}>
                <Grid item xs={12} className={classes.deleteButton}>
                  {isEditWorkMode() && (
                    <div onClick={() => handleDeleteCriteriaSetupItem(index)} style={{ display: 'inline-flex' }}>
                      <DeleteOutlineIcon style={{ width: '16px', position: 'relative', bottom: '3px', right: '5px' }} />
                      Usuń
                    </div>
                  )}
                </Grid>
                <Grid item xs={12} className={classes.item}>
                  <Grid item xs={6}>
                    <FormControl style={{ width: '340px' }} size="small">
                      <InputLabel id="label" style={{ color: '#000000', left: '14px', top: '-8px' }}>
                        Jeśli w kryterium
                      </InputLabel>
                      <Select
                        disabled={!isEditWorkMode()}
                        variant="outlined"
                        id="select"
                        labelId="label"
                        value={criterion.leadingSchema.id}
                        label="Jeśli w kryterium"
                        aria-label="Jeśli w kryterium"
                        onChange={(event) => {
                          handleLeadingCriterionChange(event, criterion);
                        }}
                        inputProps={{
                          color: '#000000',
                          padding: '5px',
                        }}
                        startAdornment={blockIconForInput(!isEditWorkMode(), { marginRight: '0px' })}
                      >
                        {criteriaSetupTemplate.leadingSchemas.map((scheme: Scheme) => (
                          <MenuItem disableRipple key={scheme.id} value={scheme.id}>
                            {scheme.name}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid item xs={1} className={classes.subTitle}>
                    przyznano <br />
                    (pkt):
                  </Grid>
                  <Grid item xs={1}>
                    <FormControl style={{ width: '60px' }} size="small">
                      <InputLabel id="label" style={InputLabelStyle(criterion.leadingSchema.id)}>
                        Od
                      </InputLabel>
                      <Select
                        disabled={criterion.leadingSchema.id === '' || !isEditWorkMode()}
                        variant="outlined"
                        id="select"
                        labelId="label"
                        label="Od"
                        value={criterion.fromExamScoreValueForLeadingSchema.id}
                        aria-label="punkty od"
                        onChange={(event) => {
                          handleLeadingCriterioScoresChangeFrom(event, criterion);
                        }}
                        startAdornment={blockIconForInput(criterion.leadingSchema.id === '' || !isEditWorkMode(), {
                          marginRight: '-5px',
                          marginLeft: '-7px',
                          fontSize: 'small',
                        })}
                        inputProps={{
                          style: {
                            padding: '5px',
                            color: '#2C2D3E',
                          },
                        }}
                      >
                        {criteriaSetupTemplate &&
                          criteriaSetupTemplate.leadingSchemas
                            .find((e) => e.id === criterion.leadingSchema.id)
                            ?.scores.map((score: Score) => (
                              <MenuItem disableRipple key={score.id} value={score.id}>
                                {score.value}
                              </MenuItem>
                            ))}
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid item xs={1}>
                    <FormControl style={{ width: '60px' }} size="small">
                      <InputLabel id="label" style={InputLabelStyle(criterion.fromExamScoreValueForLeadingSchema.id)}>
                        Do
                      </InputLabel>
                      <Select
                        disabled={criterion.fromExamScoreValueForLeadingSchema.id === '' || !isEditWorkMode()}
                        variant="outlined"
                        id="select"
                        labelId="label"
                        label="Do"
                        value={criterion.toExamScoreValueForLeadingSchema.id}
                        aria-label="punkty do"
                        onChange={(event) => {
                          handleLeadingCriterioScoresChangeTo(event, criterion);
                        }}
                        startAdornment={blockIconForInput(
                          criterion.fromExamScoreValueForLeadingSchema.id === '' || !isEditWorkMode(),
                          { marginRight: '-5px', marginLeft: '-7px', fontSize: 'small' },
                        )}
                        inputProps={{
                          style: {
                            color: '#2C2D3E',
                          },
                        }}
                      >
                        {criteriaSetupTemplate.leadingSchemas
                          .find((e) => e.id === criterion.leadingSchema.id)
                          ?.scores.map((score: Score) => (
                            <MenuItem
                              disableRipple
                              key={score.id}
                              value={score.id}
                              disabled={isScoreDisabled(criterion.fromExamScoreValueForLeadingSchema.value, score.id)}
                            >
                              {score.value}
                            </MenuItem>
                          ))}
                      </Select>
                    </FormControl>
                  </Grid>
                </Grid>

                <Grid item xs={12} className={classes.item}>
                  <Grid item xs={6}>
                    <FormControl style={{ width: '340px' }} size="small">
                      <InputLabel id="label" style={InputLabelStyle(criterion.toExamScoreValueForLeadingSchema.id)}>
                        To w kryterium
                      </InputLabel>
                      <Select
                        disabled={criterion.toExamScoreValueForLeadingSchema.id === '' || !isEditWorkMode()}
                        variant="outlined"
                        id="select"
                        labelId="label"
                        label="To w kryterium"
                        aria-label="To w kryterium"
                        value={criterion.dependentSchema.id}
                        onChange={(event) => {
                          handleDependentCriterionChange(event, criterion);
                        }}
                        startAdornment={blockIconForInput(
                          criterion.toExamScoreValueForLeadingSchema.id === '' || !isEditWorkMode(),
                          { marginRight: '0px' },
                        )}
                        inputProps={{
                          style: {
                            color: '#000000',
                          },
                        }}
                      >
                        {criteriaSetupTemplate.dependentSchemas.map((scheme: Scheme) => (
                          <MenuItem disableRipple key={scheme.id} value={scheme.id}>
                            {scheme.name}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid item xs={1} className={classes.subTitle}>
                    przyznano <br />
                    (pkt):
                  </Grid>
                  <Grid item xs={1}>
                    <FormControl style={{ width: '60px' }} size="small">
                      <InputLabel id="label" style={InputLabelStyle(criterion.dependentSchema.id)}>
                        Od
                      </InputLabel>
                      <Select
                        disabled={criterion.dependentSchema.id === '' || !isEditWorkMode()}
                        variant="outlined"
                        id="select"
                        labelId="label"
                        label="Od"
                        aria-label="punkty od"
                        value={criterion.fromExamScoreValueForDependentSchema.id}
                        onChange={(event) => {
                          handleDependentCriterioScoresChangeFrom(event, criterion);
                        }}
                        startAdornment={blockIconForInput(criterion.dependentSchema.id === '' || !isEditWorkMode(), {
                          marginRight: '-5px',
                          marginLeft: '-7px',
                          fontSize: 'small',
                        })}
                        inputProps={{
                          style: {
                            color: '#2C2D3E',
                          },
                        }}
                      >
                        {criteriaSetupTemplate.dependentSchemas
                          .find((e) => e.id === criterion.dependentSchema.id)
                          ?.scores.map((score: Score) => (
                            <MenuItem disableRipple key={score.id} value={score.id}>
                              {score.value}
                            </MenuItem>
                          ))}
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid item xs={1}>
                    <FormControl style={{ width: '60px' }} size="small">
                      <InputLabel id="label" style={InputLabelStyle(criterion.fromExamScoreValueForDependentSchema.id)}>
                        Do
                      </InputLabel>
                      <Select
                        disabled={criterion.fromExamScoreValueForDependentSchema.id === '' || !isEditWorkMode()}
                        variant="outlined"
                        id="select"
                        labelId="label"
                        label="Do"
                        aria-label="punkty do"
                        value={criterion.toExamScoreValueForDependentSchema.id}
                        onChange={(event) => handleDependentCriterioScoresChangeTo(event, criterion)}
                        startAdornment={blockIconForInput(
                          criterion.fromExamScoreValueForDependentSchema.id === '' || !isEditWorkMode(),
                          { marginRight: '-5px', marginLeft: '-7px', fontSize: 'small' },
                        )}
                        inputProps={{
                          style: {
                            color: '#2C2D3E',
                          },
                        }}
                      >
                        {criteriaSetupTemplate.dependentSchemas
                          .find((e) => e.id === criterion.dependentSchema.id)
                          ?.scores.map((score: Score) => (
                            <MenuItem
                              disableRipple
                              key={score.id}
                              value={score.id}
                              disabled={isScoreDisabled(criterion.fromExamScoreValueForDependentSchema.value, score.id)}
                            >
                              {score.value}
                            </MenuItem>
                          ))}
                      </Select>
                    </FormControl>
                  </Grid>
                </Grid>

                <Grid item xs={12} style={{ maxHeight: '20px', paddingLeft: '20px' }}>
                  <hr className={classes.line}></hr>
                </Grid>
              </React.Fragment>
            ))}
          </div>
          {isAddButton && (
            <Grid item xs={12} style={{ maxHeight: '60px', padding: '10px 20px' }}>
              <ButtonOne
                className={classes.addButton}
                variant="contained"
                aria-label="Dodaj"
                startIcon={<AddIcon style={{ fontSize: 14 }} />}
                onClick={handleAddCriteriaSetupItem}
              >
                Dodaj
              </ButtonOne>
            </Grid>
          )}

          <Grid container justifyContent="flex-end">
            {props.workMode !== WorkMode.VIEW && (
              <ActionButtons
                disable={isSaveDisabled()}
                saveHandle={saveChanges}
                closeHandle={() => setIsLeavePromptOpen(true)}
              />
            )}
          </Grid>
        </Grid>
      )}
      <LeavePrompt isOpen={isLeavePromptOpen} onConfirm={closeHandle} onCancel={() => setIsLeavePromptOpen(false)} />

      {saveValidationError !== null && (
        <Prompt
          type="warning"
          open
          isBold
          onConfirm={() => setSaveValidationError(null)}
          mainText={getMainText()}
          subTexts={getSubTexts()}
          subTexts2={
            saveValidationError === 'ExamScoringValuesAreCovering'
              ? [
                  {
                    text: 'ponieważ nakładają się zbiory wiodące w tej samej parze kryteriów.',
                    isBold: true,
                  },
                ]
              : undefined
          }
        />
      )}
    </>
  );
};

export default CriteriaSetupDetails;
